import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

// ─── Blog Data (ordered latest first - May 2026 articles at the top) ────────
const blogPosts = [
  // ==================== MAY 2026 ARTICLES (5 new) ====================
  // ── Article 24: Schools and Universities (May 31, 2026)
  {
    id: 24,
    slug: "how-schools-and-universities-in-kenya-can-improve-communication-using-bulk-email",
    image: "/Blogs/24.png",
    date: "31 MAY 2026",
    category: "REGIONAL",
    title: "How Schools and Universities in Kenya Can Improve Communication Using Bulk Email",
    description: "Discover how schools and universities in Kenya can use bulk email to improve communication with students, parents, and stakeholders efficiently.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Educational institutions in Kenya manage communication across multiple groups daily; students, parents, teachers, administrative staff, and external stakeholders.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          From admission updates and fee reminders to exam schedules and urgent announcements, the volume of communication is high. Without a structured system, important messages are often delayed, missed, or inconsistently delivered.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Bulk email marketing provides schools and universities with a reliable, scalable, and professional communication channel that ensures the right message reaches the right audience at the right time. It improves efficiency, reduces administrative pressure, and strengthens engagement across the entire academic ecosystem.
        </p>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">How Schools and Universities Use Bulk Email Effectively</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">1. Academic Announcements and Institutional Updates</p>
              <p className="text-gray-700 leading-relaxed">Bulk email allows institutions to share key academic information quickly and consistently, such as exam timetables, admission and enrollment updates, fee payment deadlines, and academic calendar changes. Instead of relying on notice boards or scattered communication channels, email ensures every student and parent receives the same information instantly.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">2. Parent and Guardian Communication</p>
              <p className="text-gray-700 leading-relaxed">Strong parent engagement improves student performance and institutional trust. Bulk email helps schools maintain continuous communication through academic performance updates, event invitations, behavioural or disciplinary notices, and school newsletters.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">3. Student Engagement and Academic Support</p>
              <p className="text-gray-700 leading-relaxed">Institutions can use email to keep students informed, engaged, and supported by sending learning materials, club updates, internship opportunities, and campus event notifications.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">4. Emergency and Urgent Communication</p>
              <p className="text-gray-700 leading-relaxed">For urgent updates like school closures or security alerts, bulk email provides a fast and reliable channel. Combined with SMS, institutions ensure critical messages are received instantly.</p>
            </div>
          </div>
        </div>

        <div className="mb-8 bg-green-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-green-600 mb-3">Advanced Strategies for Effective School Communication</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Audience Segmentation</p>
              <p className="text-gray-700 leading-relaxed">Schools can categorize email lists into prospective students, current students, parents, and staff for personalized communication.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Automating Administrative Communication</p>
              <p className="text-gray-700 leading-relaxed">Automate application acknowledgments, admission notifications, fee reminders, and onboarding sequences to reduce manual workload.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Mobile-Optimized Communication</p>
              <p className="text-gray-700 leading-relaxed">Ensure responsive designs, clear formatting, and clickable buttons for the many parents and students accessing emails via mobile devices.</p>
            </div>
          </div>
        </div>

        <div className="mb-8 bg-purple-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-purple-600 mb-3">Key Benefits</h3>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Faster and more structured communication</li>
            <li>• Improved engagement with students and parents</li>
            <li>• Reduced administrative workload</li>
            <li>• Professional and consistent messaging</li>
            <li>• Measurable communication performance through analytics</li>
          </ul>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Modern Education Requires Modern Communication Systems</h3>
          <p className="leading-relaxed">
            Infinity EmailPro by Infinity Tech Africa helps schools and universities send announcements, automate communication workflows, and manage stakeholder engagement efficiently. Build a smarter communication system today with Infinity EmailPro.
          </p>
        </div>
      </article>
    )
  },
  // ── Article 23: Real Estate Lead Generation (May 28, 2026)
  {
    id: 23,
    slug: "how-real-estate-companies-in-kenya-can-generate-more-leads-using-bulk-email",
    image: "/Blogs/23.png",
    date: "28 MAY 2026",
    category: "REGIONAL",
    title: "How Real Estate Companies in Kenya Can Generate More Leads Using Bulk Email",
    description: "Learn how real estate businesses in Kenya can use bulk email marketing to generate leads, nurture buyers, and increase property sales.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          In Kenya's competitive real estate market, generating leads is only the beginning. The real challenge is staying connected with potential buyers long enough to convert interest into actual property sales.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Many real estate companies lose opportunities because follow-ups are inconsistent, communication is delayed, or potential buyers lose interest before making a decision. Since purchasing property is a major financial commitment, most buyers take time before committing.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Bulk email marketing helps real estate businesses maintain direct communication with buyers, investors, and property seekers while nurturing trust throughout the decision-making journey.
        </p>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">How Bulk Email Helps Generate More Leads</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">1. Share Property Listings Instantly</p>
              <p className="text-gray-700 leading-relaxed">Send new listings, price updates, and investment opportunities directly to prospects' inboxes.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">2. Nurture Leads Over Time</p>
              <p className="text-gray-700 leading-relaxed">Stay top-of-mind with consistent updates, building trust gradually until prospects are ready to buy.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">3. Segment Buyers for Better Targeting</p>
              <p className="text-gray-700 leading-relaxed">Group leads by budget, location preference, property type, or investment goals for relevant messaging.</p>
            </div>
          </div>
        </div>

        <div className="mb-8 bg-green-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-green-600 mb-3">Additional Strategies</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Promote Site Visits and Events</p>
              <p className="text-gray-700 leading-relaxed">Use email to promote open houses, property launches, and virtual tours with clear CTAs.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Share Valuable Insights</p>
              <p className="text-gray-700 leading-relaxed">Build credibility by sharing market trends, mortgage guidance, and neighborhood insights.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Automate Follow-Ups</p>
              <p className="text-gray-700 leading-relaxed">Set up automated sequences for welcome emails, property recommendations, and consultation invitations.</p>
            </div>
          </div>
        </div>

        <div className="mb-8 bg-purple-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-purple-600 mb-3">Key Tips for Better Campaigns</h3>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Design emails for mobile devices</li>
            <li>• Use high-quality property images</li>
            <li>• Include clear calls-to-action (CTAs)</li>
            <li>• Keep messaging simple and professional</li>
            <li>• Personalise communication where possible</li>
            <li>• Track email performance and engagement</li>
          </ul>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Generate More Real Estate Leads Today</h3>
          <p className="leading-relaxed">
            Infinity EmailPro helps real estate companies send targeted campaigns, automate follow-ups, and manage subscriber lists. Contact Infinity Tech Africa today.
          </p>
        </div>
      </article>
    )
  },
  // ── Article 22: Personalized Email Marketing (May 25, 2026)
  {
    id: 22,
    slug: "why-personalized-email-marketing-increases-sales-for-kenyan-businesses",
    image: "/Blogs/22.png",
    date: "25 MAY 2026",
    category: "REGIONAL",
    title: "Why Personalized Email Marketing Increases Sales for Kenyan Businesses",
    description: "Discover how personalized email marketing helps Kenyan businesses improve engagement, increase conversions, and boost customer loyalty.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Generic marketing messages no longer deliver strong results.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Customers expect businesses to communicate in ways that feel relevant and personalised. Bulk email marketing allows businesses to tailor communication based on customer behaviour, interests, and preferences.
        </p>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">Why Personalisation Matters</h3>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Increase open rates</li>
            <li>• Improve click-through rates</li>
            <li>• Boost conversions</li>
            <li>• Strengthen customer loyalty</li>
          </ul>
        </div>

        <div className="mb-8 bg-green-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-green-600 mb-3">How to Personalise Email Campaigns</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Audience Segmentation</p>
              <p className="text-gray-700 leading-relaxed">Divide subscribers by purchase history, interests, location, and behaviour.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Personalised Recommendations</p>
              <p className="text-gray-700 leading-relaxed">Recommend products based on previous interactions.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Behaviour-Based Emails</p>
              <p className="text-gray-700 leading-relaxed">Automate emails for cart abandonment, website visits, and sign-ups.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Localised Messaging</p>
              <p className="text-gray-700 leading-relaxed">Reference local events, seasons, and regional offers for Kenyan audiences.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Increase Engagement with Personalised Campaigns</h3>
          <p className="leading-relaxed">
            Infinity EmailPro helps businesses segment audiences, automate communication, and deliver targeted campaigns. Contact Infinity Tech Africa today.
          </p>
        </div>
      </article>
    )
  },
  // ── Article 21: Build Customer Trust (May 22, 2026)
  {
    id: 21,
    slug: "how-kenyan-businesses-can-use-bulk-email-to-build-customer-trust",
    image: "/Blogs/21.png",
    date: "22 MAY 2026",
    category: "REGIONAL",
    title: "How Kenyan Businesses Can Use Bulk Email to Build Customer Trust",
    description: "Learn how Kenyan businesses can use bulk email marketing to build trust, strengthen relationships, and improve long-term customer loyalty.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Trust has become one of the most important factors influencing customer loyalty, repeat purchases, and long-term business growth.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Bulk email marketing gives Kenyan businesses a direct and reliable way to build and maintain that trust over time.
        </p>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">Why Customer Trust Matters</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Customers who trust your business are more likely to:
          </p>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Buy repeatedly</li>
            <li>• Recommend your business to others</li>
            <li>• Engage with your communication</li>
            <li>• Remain loyal even when competitors appear</li>
          </ul>
        </div>

        <div className="mb-8 bg-green-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-green-600 mb-3">How Bulk Email Builds Trust</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Consistent Communication</p>
              <p className="text-gray-700 leading-relaxed">Regular newsletters, updates, and announcements keep your brand relevant and reliable.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Valuable Content</p>
              <p className="text-gray-700 leading-relaxed">Share educational tips, industry updates, and customer success stories.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Personalisation</p>
              <p className="text-gray-700 leading-relaxed">Use customer names, preferences, and localised messaging to show you value them.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Professional Branding</p>
              <p className="text-gray-700 leading-relaxed">Use branded email addresses (info@yourbusiness.co.ke) instead of Gmail.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Build Stronger Customer Relationships</h3>
          <p className="leading-relaxed">
            Infinity EmailPro helps businesses create reliable campaigns and improve customer engagement. Contact Infinity Tech Africa today.
          </p>
        </div>
      </article>
    )
  },
  // ── Article 20: Professional Bulk Email Platform vs Gmail (May 19, 2026)
  {
    id: 20,
    slug: "why-your-business-needs-a-professional-bulk-email-platform-instead-of-regular-gmail",
    image: "/Blogs/20.png",
    date: "19 MAY 2026",
    category: "REGIONAL",
    title: "Why Your Business Needs a Professional Bulk Email Platform Instead of Regular Gmail",
    description: "Still sending marketing emails using Gmail? Learn why businesses need a professional bulk email platform for better deliverability, automation, and scalability.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Many businesses start by sending emails through personal Gmail accounts. While this may work initially, it becomes ineffective as your business grows.
        </p>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">Why Gmail Is Not Enough</h3>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Daily email sending limits</li>
            <li>• Poor scalability for growing needs</li>
            <li>• Limited automation capabilities</li>
            <li>• Higher chances of landing in spam</li>
            <li>• Difficulty managing large subscriber lists</li>
          </ul>
        </div>

        <div className="mb-8 bg-green-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-green-600 mb-3">Benefits of a Professional Platform</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Better Deliverability</p>
              <p className="text-gray-700 leading-relaxed">Features like sender authentication and domain verification help emails reach inboxes.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Bulk Sending at Scale</p>
              <p className="text-gray-700 leading-relaxed">Send thousands of emails efficiently without account restrictions.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Automation and Scheduling</p>
              <p className="text-gray-700 leading-relaxed">Automate welcome emails, follow-ups, onboarding, and cart reminders.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Analytics and Segmentation</p>
              <p className="text-gray-700 leading-relaxed">Track open rates, click-through rates, and segment audiences for targeted messaging.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Upgrade to a Professional Bulk Email Platform</h3>
          <p className="leading-relaxed">
            Infinity EmailPro helps businesses send campaigns instantly, automate communication, and improve deliverability. Contact Infinity Tech Africa today.
          </p>
        </div>
      </article>
    )
  },

  // ==================== EXISTING ARTICLES (keeping all your original content) ====================
  // ── Article 19: Are Your Emails Going to Spam?
  {
    id: 19,
    slug: "how-kenyan-businesses-can-improve-email-durability",
    image: "/Blogs/19.png",
    date: "30 APR 2026",
    category: "REGIONAL",
    title: "Are Your Emails Going to Spam? How Kenyan Businesses Can Improve Email Deliverability",
    description: "Are your emails going to spam? Learn how Kenyan businesses can improve email deliverability, increase inbox placement, and boost engagement.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          If your emails are not reaching the inboxes of your target audiences, your campaigns are not working.
        </p>
        <p className="text-gray-700 leading-relaxed mb-8">
          Many Kenyan businesses invest in email marketing but struggle with low open rates, not because of poor content, but because emails are landing in spam folders.
        </p>

        <div className="mb-8 bg-red-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-red-600 mb-3">Common Causes of Emails Going to Spam</h3>
          <ul className="text-gray-700 space-y-2 ml-4">
            <li>• Sending emails to unverified or outdated lists</li>
            <li>• Using spam-trigger words in subject lines</li>
            <li>• Lack of subscriber consent (no opt-in)</li>
            <li>• Poor sender reputation</li>
            <li>• No proper authentication</li>
          </ul>
        </div>

        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">What Proper Email Authentication Means</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">SPF (Sender Policy Framework)</p>
              <p className="text-gray-700 leading-relaxed">Defines which servers can send emails on behalf of your domain.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">DKIM (DomainKeys Identified Mail)</p>
              <p className="text-gray-700 leading-relaxed">Adds a digital signature to confirm emails haven't been altered.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">DMARC</p>
              <p className="text-gray-700 leading-relaxed">Tells email providers what to do if SPF or DKIM fails and provides reporting.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Stop Losing Opportunities to Spam Folders</h3>
          <p className="leading-relaxed">
            Infinity EmailPro helps you send reliable, high-deliverability email campaigns. Contact Infinity Tech Africa today.
          </p>
        </div>
      </article>
    )
  },
  // ── Article 18: Bulk Email for E-commerce in Kenya
  {
    id: 18,
    slug: "bulk-email-for-ecommerce-businesses-in-kenya-to-increase-revenue",
    image: "/Blogs/18.png",
    date: "24 APR 2026",
    category: "REGIONAL",
    title: "Low Online Sales? How E-commerce Businesses in Kenya Can Increase Revenue with Bulk Email",
    description: "Struggling with low online sales? Learn how bulk email helps e-commerce businesses in Kenya boost conversions and customer engagement.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          If your online store is getting traffic but not generating enough sales, you're not alone.
        </p>
        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">How Bulk Email Increases E-commerce Sales</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">1. Abandoned Cart Reminders</p>
              <p className="text-gray-700 leading-relaxed">Recover lost sales with automated cart recovery emails.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">2. Personalised Product Recommendations</p>
              <p className="text-gray-700 leading-relaxed">Suggest products based on browsing and purchase history.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">3. Promotions and Discount Campaigns</p>
              <p className="text-gray-700 leading-relaxed">Drive urgency with flash sales and limited-time offers.</p>
            </div>
          </div>
        </div>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Boost Your Online Sales</h3>
          <p className="leading-relaxed">Infinity EmailPro helps e-commerce businesses automate communication and recover lost sales.</p>
        </div>
      </article>
    )
  },
  // ── Article 17: Bulk Email for NGOs in Kenya
  {
    id: 17,
    slug: "bulk-email-for-kenyan-ngos",
    image: "/Blogs/17.png",
    date: "16 APR 2026",
    category: "REGIONAL",
    title: "How NGOs in Kenya Can Reach More Donors and Communities Using Bulk Email",
    description: "Learn how NGOs in Kenya use bulk email to reach donors, improve communication, and boost community engagement effectively.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          For NGOs in Kenya, effective communication is essential for reaching donors, engaging stakeholders, and supporting communities.
        </p>
        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">How Bulk Email Helps NGOs</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Donor Updates and Newsletters</p>
              <p className="text-gray-700 leading-relaxed">Keep donors informed about project progress and impact stories.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Fundraising and Campaign Awareness</p>
              <p className="text-gray-700 leading-relaxed">Promote fundraising campaigns and advocacy initiatives.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Community Engagement</p>
              <p className="text-gray-700 leading-relaxed">Share important program announcements and health information.</p>
            </div>
          </div>
        </div>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Improve Your NGO's Outreach</h3>
          <p className="leading-relaxed">Contact Infinity Tech Africa to learn how Infinity EmailPro can help your organisation.</p>
        </div>
      </article>
    )
  },
  // ── Article 16: Bulk Email for Lead Conversion in Kenya
  {
    id: 16,
    slug: "how-bulk-email-marketing-helps-kenyan-businesses-turn-leads-into-customers",
    image: "/Blogs/16.png",
    date: "10 APR 2026",
    category: "REGIONAL",
    title: "Not Getting Enough Conversions? How Bulk Email Helps Kenyan Businesses Turn Leads into Customers",
    description: "Not getting enough conversions? Learn how bulk email helps Kenyan businesses nurture leads and turn prospects into paying customers.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Getting leads is one thing, converting them into paying customers is another.
        </p>
        <div className="mb-8 bg-blue-50 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-blue-600 mb-3">How Bulk Email Supports Lead Nurturing</h3>
          <div className="space-y-4">
            <div>
              <p className="text-gray-700 font-semibold">Educational Content Builds Trust</p>
              <p className="text-gray-700 leading-relaxed">Share helpful content that explains your services and solutions.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Structured Email Sequences</p>
              <p className="text-gray-700 leading-relaxed">Create automated flows that introduce your brand and address objections.</p>
            </div>
            <div>
              <p className="text-gray-700 font-semibold">Social Proof Increases Confidence</p>
              <p className="text-gray-700 leading-relaxed">Use case studies and testimonials to reassure potential customers.</p>
            </div>
          </div>
        </div>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Turn Your Leads into Paying Customers</h3>
          <p className="leading-relaxed">Infinity EmailPro helps you automate lead nurturing and convert prospects more effectively.</p>
        </div>
      </article>
    )
  },
  // ── Article 15: Bulk Email for Customer Retention in Kenya
  {
    id: 15,
    slug: "how-bulk-email-marketing-helps-kenyan-businesses-increase-repeat-sales",
    image: "/Blogs/15.png",
    date: "1 APR 2026",
    category: "REGIONAL",
    title: "Struggling to Retain Customers? How Bulk Email Marketing Helps Kenyan Businesses Increase Repeat Sales",
    description: "Struggling to retain customers? Learn how bulk email marketing helps Kenyan businesses increase repeat sales.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          If your customers buy once and never return, your business is losing revenue.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Turn One-Time Buyers into Loyal Customers</h3>
          <p className="leading-relaxed">Infinity EmailPro helps you create targeted campaigns that keep customers coming back.</p>
        </div>
      </article>
    )
  },
  // ── Article 14: How to Create an Effective Bulk Email Marketing Campaign
  {
    id: 14,
    slug: "effective-bulk-email-marketing-campaign",
    image: "/Blogs/14.png",
    date: "17 MAR 2026",
    category: "GLOBAL",
    title: "How to Create an Effective Bulk Email Marketing Campaign",
    description: "Learn how to create an effective bulk email marketing campaign with practical strategies.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          A well-planned bulk email campaign allows organisations to promote products, share updates, and nurture customer relationships.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Ready to Launch Your Campaign?</h3>
          <p className="leading-relaxed">Infinity EmailPro helps businesses design professional campaigns and track performance.</p>
        </div>
      </article>
    )
  },
  // ── Article 13: How Email Automation Helps Businesses
  {
    id: 13,
    slug: "email-automation-for-business-to-increase-sales",
    image: "/Blogs/13.png",
    date: "12 MAR 2026",
    category: "GLOBAL",
    title: "How Email Automation Helps Businesses Save Time and Increase Sales",
    description: "Learn how email automation helps businesses streamline communication and increase sales.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Email automation has become an essential tool for businesses looking to streamline communication.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Streamline Your Communication Today</h3>
          <p className="leading-relaxed">Infinity EmailPro provides powerful automation tools for your business.</p>
        </div>
      </article>
    )
  },
  // ── Article 12: Email Subject Lines
  {
    id: 12,
    slug: "email-subject-lines-open-rates",
    image: "/Blogs/12.png",
    date: "07 MAR 2026",
    category: "GLOBAL",
    title: "How to Write Email Subject Lines That Increase Open Rates",
    description: "Learn how to write effective email subject lines that increase open rates.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Email subject lines determine whether recipients open your email or ignore it.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Improve Your Email Open Rates Today</h3>
          <p className="leading-relaxed">Infinity EmailPro helps you create professional campaigns that reach your audience effectively.</p>
        </div>
      </article>
    )
  },
  // ── Article 11: 7 Email Marketing Mistakes
  {
    id: 11,
    slug: "email-marketing-mistakes-for-kenyan-businesses-to-avoid",
    image: "/Blogs/11.png",
    date: "03 MAR 2026",
    category: "REGIONAL",
    title: "7 Email Marketing Mistakes Kenyan Businesses Should Avoid",
    description: "Avoid common email marketing mistakes that reduce engagement and campaign performance.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Businesses use email marketing to communicate with customers, but many fail due to common mistakes.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Strengthen Your Email Marketing Strategy</h3>
          <p className="leading-relaxed">Infinity EmailPro helps you send campaigns and track performance with ease.</p>
        </div>
      </article>
    )
  },
  // ── Article 10: Bulk Email Analytics
  {
    id: 10,
    slug: "bulk-email-analytics-kenya",
    image: "/Blogs/10.png",
    date: "03 MAR 2026",
    category: "GLOBAL",
    title: "Bulk Email Analytics: How Kenyan Businesses Can Measure and Improve Campaign Success",
    description: "Understand the key analytics metrics Kenyan businesses should track to improve engagement and ROI.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Bulk email marketing is only effective when results are measured using data.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Start Sending Emails That Deliver Real Results</h3>
          <p className="leading-relaxed">Infinity Tech Africa helps Kenyan businesses track, optimise, and scale email campaigns.</p>
        </div>
      </article>
    )
  },
  // ── Article 9: Why Bulk Email Still Works
  {
    id: 9,
    slug: "why-bulk-email-marketing-still-works-for-kenyan-businesses-in-2026",
    image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=2340&q=80",
    date: "27 FEB 2026",
    category: "REGIONAL",
    title: "Why Bulk Email Marketing Still Works for Kenyan Businesses in 2026",
    description: "Discover why bulk email marketing remains a powerful communication tool for Kenyan businesses in 2026.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Bulk email marketing remains one of the most effective ways for Kenyan organisations to engage with customers.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Power Your Business with Bulk Email</h3>
          <p className="leading-relaxed">Infinity EmailPro helps organisations send campaigns instantly and track performance.</p>
        </div>
      </article>
    )
  },
  // ── Article 8: Build and Grow Email List
  {
    id: 8,
    slug: "bulk-email-list-building-kenya",
    image: "https://images.unsplash.com/photo-1512314889357-e157c22f938d?auto=format&fit=crop&w=2340&q=80",
    date: "27 FEB 2026",
    category: "REGIONAL",
    title: "How Kenyan Businesses Can Build and Grow a High-Quality Email List",
    description: "Learn ethical and effective email list building strategies for Kenyan businesses.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          A successful bulk email strategy depends on the quality of your email list.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Build an Email List That Converts</h3>
          <p className="leading-relaxed">Infinity Tech Africa provides secure, scalable bulk email solutions.</p>
        </div>
      </article>
    )
  },
  // ── Article 7: Best Practices
  {
    id: 7,
    slug: "bulk-email-best-practices-kenya",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2340&q=80",
    date: "23 FEB 2026",
    category: "GLOBAL",
    title: "Best Practices for High-Performing Bulk Email Campaigns in Kenya",
    description: "Learn proven best practices to improve bulk email campaign performance in Kenya.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Sending bulk emails is easy, but achieving consistent results requires strategy.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Optimise Your Email Campaign Performance</h3>
          <p className="leading-relaxed">Infinity Tech Africa helps businesses optimize high-performing campaigns.</p>
        </div>
      </article>
    )
  },
  // ── Article 6: Benefits for SMEs
  {
    id: 6,
    slug: "bulk-email-marketing-benefits-kenya",
    image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=2340&q=80",
    date: "20 FEB 2026",
    category: "REGIONAL",
    title: "Top Benefits of Bulk Email Marketing for SMEs in Kenya",
    description: "Discover the key benefits of bulk email marketing for Kenyan SMEs.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          SMEs in Kenya face increasing pressure to communicate consistently with customers.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Grow Your Business with Bulk Email</h3>
          <p className="leading-relaxed">Reach out to Infinity Tech Africa for reliable bulk email solutions.</p>
        </div>
      </article>
    )
  },
  // ── Article 5: Step-by-Step Guide
  {
    id: 5,
    slug: "how-bulk-email-marketing-works-kenya",
    image: "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?auto=format&fit=crop&w=2340&q=80",
    date: "16 FEB 2026",
    category: "REGIONAL",
    title: "How Bulk Email Marketing Works for Kenyan Businesses: A Practical Step-by-Step Guide",
    description: "Discover how bulk email marketing works for Kenyan businesses.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Bulk email marketing remains one of the most reliable communication channels for Kenyan businesses.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Ready to Launch Your Bulk Email Campaigns?</h3>
          <p className="leading-relaxed">Talk to Infinity Tech Africa for business-ready bulk email solutions.</p>
        </div>
      </article>
    )
  },
  // ── Article 4: API Development Tools
  {
    id: 4,
    slug: "api-development-tools",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
    date: "13 FEB 2026",
    category: "REGIONAL",
    title: "API Development Tools",
    description: "We've assembled a collection of tools specifically designed to simplify your journey in using our APIs.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          We've assembled a collection of tools specifically designed to simplify your journey in using our APIs.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Get Started Today</h3>
          <p className="leading-relaxed">Contact our developer support team to get access to our sandbox environment.</p>
        </div>
      </article>
    )
  },
  // ── Article 3: USSD Setup
  {
    id: 3,
    slug: "how-to-set-up-a-ussd-code-for-your-business",
    image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2339&q=80",
    date: "08 FEB 2026",
    category: "GLOBAL",
    title: "How to Set Up a USSD Code for Your Business",
    description: "Setting up a USSD code may sound technical, but with the right partner, it's a smooth process.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Setting up a USSD code may sound technical, but with the right partner, it's a smooth process.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Get Your USSD Code Today</h3>
          <p className="leading-relaxed">Contact Infinity Tech Africa for reliable USSD solutions.</p>
        </div>
      </article>
    )
  },
  // ── Article 2: SMS Marketing Checklist
  {
    id: 2,
    slug: "sms-marketing-checklist-for-kenyan-businesses",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
    date: "06 FEB 2026",
    category: "GLOBAL",
    title: "SMS Marketing Checklist for Kenyan Businesses",
    description: "SMS marketing remains one of the most powerful tools for businesses to connect with customers.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          SMS marketing remains one of the most powerful tools for businesses to connect with customers.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Start Your SMS Marketing Journey</h3>
          <p className="leading-relaxed">Infinity Tech Africa provides reliable Bulk SMS solutions.</p>
        </div>
      </article>
    )
  },
  // ── Article 1: 5 Ways Bulk SMS Boosts Sales
  {
    id: 1,
    slug: "5-ways-bulk-sms-boosts-small-business-sales",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
    date: "01 FEB 2026",
    category: "REGIONAL",
    title: "5 Ways Bulk SMS Boosts Small Business Sales",
    description: "Bulk SMS marketing remains one of the most effective communication tools available in Kenya.",
    content: (
      <article className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed mb-6">
          Bulk SMS marketing remains one of the most effective communication tools available in Kenya.
        </p>
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Why Choose Infinity Tech Africa for Bulk SMS?</h3>
          <p className="leading-relaxed">We specialise in reliable, affordable, and scalable Bulk SMS solutions.</p>
        </div>
      </article>
    )
  },
];

// ─── Footer Component (unchanged) ────────────────────────────────────────────
const Footer = () => (
  <footer className="bg-blue-600 text-white py-12">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 border-b border-blue-500 pb-10">
        <div className="space-y-3 md:border-r border-white md:pr-6">
          <h3 className="text-lg font-bold mb-2">About Us</h3>
          <ul className="text-sm space-y-2">
            <li><a href="/about" className="hover:text-blue-200">Who we are</a></li>
            <li><a href="/about" className="hover:text-blue-200">News Hub</a></li>
            <li><a href="/about" className="hover:text-blue-200">Our Reach</a></li>
          </ul>
        </div>
        <div className="space-y-3 md:border-r border-white md:px-6">
          <h3 className="text-lg font-bold mb-2">Customer Service</h3>
          <ul className="text-sm space-y-2">
            <li><a href="/contact" className="hover:text-blue-200">Message Us</a></li>
            <li><a href="/contact" className="hover:text-blue-200">Comment/Complaint</a></li>
          </ul>
        </div>
        <div className="space-y-4 md:pl-6">
          <h4 className="text-sm font-semibold mb-2">Payment Options</h4>
          <div className="flex space-x-4">
            <img src="/icons/mpesa2.png" alt="M-Pesa" className="h-12 object-contain" />
            <img src="/icons/mastercard.png" alt="Mastercard" className="h-6 object-contain" />
            <img src="/icons/visa.png" alt="Visa" className="h-6 object-contain" />
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-8 mb-6">
        <div className="flex items-center space-x-6 bg-green-700 px-8 py-4 rounded-lg shadow-lg">
          <span className="text-sm font-semibold">Get in Touch:</span>
          <a href="tel:+254722943271" className="bg-white hover:bg-blue-100 p-3 rounded-full transition-all duration-300 transform hover:scale-110 shadow-md" title="Call us">
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
          </a>
          <a href="https://wa.me/254722943271" target="_blank" rel="noopener noreferrer" className="bg-white hover:bg-green-50 p-3 rounded-full transition-all duration-300 transform hover:scale-110 shadow-md" title="WhatsApp us">
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </a>
        </div>
      </div>

      <div className="flex justify-center">
        <div className="flex space-x-4 border border-white p-3 rounded">
          <a href="https://www.facebook.com/SMSAfrica01" target="_blank" rel="noopener noreferrer"><img src="/icons/facebook.png" alt="Facebook" className="w-6 h-6" /></a>
          <a href="https://www.linkedin.com/company/infinity-tech-africa/" target="_blank" rel="noopener noreferrer"><img src="/icons/linkedin.png" alt="LinkedIn" className="w-6 h-6" /></a>
          <a href="https://www.instagram.com/smsafrica/" target="_blank" rel="noopener noreferrer"><img src="/icons/email.png" alt="Instagram" className="w-6 h-6" /></a>
          <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer"><img src="/icons/x logo.png" alt="Twitter" className="w-6 h-6" /></a>
          <a href="https://www.tiktok.com/@infinitytechafrica" target="_blank" rel="noopener noreferrer"><img src="/icons/tiktok logo2.png" alt="TikTok" className="w-6 h-6" /></a>
        </div>
      </div>

      <div className="text-center pt-6 mt-6 border-t border-blue-500 text-sm">
        <p>
  &copy; 2025 Infinity Tech Africa |{' '}
  <a
    href="/privacy-policy.pdf"
    target="_blank"
    rel="noopener noreferrer"
    className="hover:text-blue-200 underline"
  >
    Privacy Policy
  </a>{' '}
  | Terms & Conditions Apply
</p>
      </div>
    </div>
  </footer>
);

// ─── OPTIMIZED Blog Listing Page (with lazy loading & performance fixes) ───
const BlogsComponent = () => {
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState({});
  const observerRef = useRef(null);

  // Set up Intersection Observer for lazy loading images
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const dataSrc = img.getAttribute('data-src');
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
            observerRef.current.unobserve(img);
          }
        }
      });
    }, { rootMargin: '100px', threshold: 0.1 });

    // Observe all lazy images
    document.querySelectorAll('.lazy-img').forEach(img => {
      observerRef.current.observe(img);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // Preload first 3 images for faster perceived load time
  useEffect(() => {
    const preloadImages = blogPosts.slice(0, 3).map(post => {
      const img = new Image();
      img.src = post.image;
      return img;
    });
    return () => {
      preloadImages.forEach(img => img.src = '');
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-blue-600 mb-4">BLOGS</h1>
        </div>

        {/* Blog Grid with Optimized Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {blogPosts.map((post, index) => (
            <div
              key={post.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group"
              onClick={() => navigate(`/blogs/${post.slug}`)}
            >
              <div className="relative h-64 overflow-hidden bg-gray-200">
                {/* Image with lazy loading */}
                <img
                  className={`lazy-img w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${loadedImages[post.id] ? 'opacity-100' : 'opacity-0'}`}
                  data-src={post.image}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                  onLoad={(e) => {
                    setLoadedImages(prev => ({ ...prev, [post.id]: true }));
                    e.target.style.opacity = '1';
                  }}
                />
                {/* Fallback placeholder while loading */}
                {!loadedImages[post.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <div className="absolute bottom-4 left-4 bg-white rounded-full px-4 py-2 shadow-md z-10">
                  <span className="text-sm font-semibold text-gray-800">
                    {post.date} | {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors duration-200 line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 leading-relaxed line-clamp-3">
                  {post.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16">
        <Footer />
      </div>
    </div>
  );
};

// ─── Individual Blog Post Page ─────────────────────────────────
export const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Post Not Found</h1>
        <p className="text-gray-500 mb-8">The blog post you're looking for doesn't exist.</p>
        <Link to="/blogs" className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
          ← Back to Blogs
        </Link>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Nav Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <Link
          to="/blogs"
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Blogs
        </Link>
        <span className="text-gray-300">|</span>
        <span className="text-sm text-gray-500 truncate">{post.title}</span>
      </div>

      {/* Hero Image with lazy loading */}
      <div className="w-full h-72 md:h-[420px] overflow-hidden bg-gray-200">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
      </div>

      {/* Article Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta */}
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
            {post.category}
          </span>
          <span className="text-sm text-gray-400">{post.date}</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-tight">
          {post.title}
        </h1>

        {/* Content */}
        {post.content}

        {/* Back link at bottom */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Blogs
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogsComponent;